import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * FinderPuzzlesPage — the "Puzzles" tile on HustleFinder lands here.
 *
 * Sudoku (live). Crosswords ship after the iOS/Android apps are approved.
 * Kid-focused activities (Mazes, Colouring) live on /finder/kids.
 */
const CARDS = [
  {
    key: "sudoku",
    to: "/finder/puzzles/sudoku",
    title: "Sudoku",
    desc: "Easy, medium and hard — play online or print A4.",
    icon: "🔢",
    live: true,
  },
  {
    key: "crossword",
    to: "/finder/puzzles/crossword",
    title: "Daily Crossword",
    desc: "Fresh Aussie-themed crossword every day. Play or print A4.",
    icon: "🔤",
    live: true,
  },
  {
    key: "jigsaw",
    to: "/finder/kids/jigsaw",
    title: "Jigsaw",
    desc: "Drag-to-solve picture puzzles — koala, Uluru, Opera House and more.",
    icon: "🧩",
    live: true,
  },
];

export default function FinderPuzzlesPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t("finder.puzzles.meta_title",
      { defaultValue: "Puzzles — HustleFinder" });
  }, [t]);

  return (
    <div
      data-testid="puzzles-page"
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white"
    >
      <div className="max-w-2xl mx-auto px-4 py-10 text-center">
        <Link
          to="/finder"
          data-testid="puzzles-back-link"
          className="text-xs text-emerald-700 hover:underline"
        >
          ← {t("finder.puzzles.back", { defaultValue: "back to search" })}
        </Link>
        <h1 className="text-3xl font-bold text-slate-900 mt-3 mb-2">
          {t("finder.puzzles.heading", { defaultValue: "Puzzles" })}
        </h1>
        <p className="text-slate-600 mb-8">
          {t("finder.puzzles.intro", { defaultValue:
            "Little breathers for the trip. Play online, or print an A4 for the road." })}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {CARDS.map((c) => {
            const inner = (
              <>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-2xl">{c.icon}</span>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {c.title}
                  </h2>
                  <span className={`ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    c.live
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-100 text-slate-500"
                  }`}>
                    {c.live ? "Play" : "Soon"}
                  </span>
                </div>
                <p className="text-sm text-slate-600">{c.desc}</p>
              </>
            );
            return c.live && c.to ? (
              <Link
                key={c.key}
                to={c.to}
                data-testid={`puzzles-card-${c.key}`}
                className="text-left bg-white border border-slate-200 rounded-2xl p-5
                            hover:border-emerald-400 hover:shadow-md transition"
              >
                {inner}
              </Link>
            ) : (
              <div
                key={c.key}
                data-testid={`puzzles-card-${c.key}`}
                className="text-left bg-white border border-slate-200 rounded-2xl p-5 opacity-70"
              >
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
